import { useEffect, useMemo, useRef, useState } from "react";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { MantineProvider, Global } from "@mantine/core";

// Chakra imports
import { Box, Flex, useColorMode, useColorModeValue } from "@chakra-ui/react";
// api calls
import { getClients } from "api/requests/getClients.js";
import { clientData } from "./makeData";
import ClientModal from "./components/ClientModal";
import "../Clients/styles.css";

function Clients({ onClientUpdate }) {
  const [isLoading, setIsLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([{ id: "accountName", desc: false }]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const fetchClients = async () => {
    try {
      const data = await getClients();
      const filteredData = data.filter((client) => client.status !== false);

      if (typeof window !== "undefined") {
        setData(clientData(filteredData));
        setIsLoading(false);
      }

      setClients(filteredData);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const handleClientUpdate = async () => {
    await fetchClients();
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const columns = [
    {
      accessorKey: "accountName",
      header: "Account Name",
      size: 230,
    },
    {
      accessorKey: "brand",
      header: "Brand",
      size: 170,
      Cell: ({ cell }) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: "blue.400",
            borderRadius: "4px",
            color: "#fff",
            maxWidth: "12ch",
            padding: "4px",
            textAlign: "center",
          }}
        >
          {cell.getValue()}
        </Box>
      ),
    },
    {
      accessorKey: "clienetwebsite",
      header: "URL",
      size: 230,
      Cell: ({ cell }) => (
        <Box onClick={(e) => e.stopPropagation()}>{cell.getValue()}</Box>
      ),
    },
    {
      accessorKey: "status",
      header: "isActive",
      size: 140,
      Cell: ({ cell }) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor:
              cell.getValue() === "Active" ? "green.400" : "yellow.400",
            borderRadius: "4px",
            color: "#fff",
            maxWidth: "12ch",
            padding: "4px",
            textAlign: "center",
          }}
        >
          {cell.getValue() === "Active" ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      accessorKey: "tasks",
      header: "Tasks Loaded?",
      size: 140,
      Cell: ({ cell }) => (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: cell.getValue() === true ? "green.400" : "red.400",
            borderRadius: "4px",
            color: "#fff",
            maxWidth: "12ch",
            padding: "4px",
            textAlign: "center",
          }}
        >
          {cell.getValue() === true ? "Yes" : "No"}
        </Box>
      ),
    },
  ];

  const table = useMantineReactTable({
    columns,
    data,
    enableBottomToolbar: true,
    enableColumnResizing: true,
    enableGlobalFilterModes: true,
    enablePagination: true,
    enablePinning: true,
    enableFacetedValues: true,
    enableDensityToggle: false,
    mantineTableProps: {
      sx: {
        backgroundColor: "dark",
      },
    },
    mantineTableContainerProps: {
      sx: { maxHeight: "615px" },
    },
    onSortingChange: setSorting,
    mantinePaperProps: {
      style: { "--mrt-base-background-color": "rgb(33, 24, 44)" },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        row.getToggleSelectedHandler()(e);
        handleRowClick(row.original);
      },
      sx: {
        cursor: "pointer",
        "--mrt-base-background-color": "rgb(33, 24, 44)",
      },
      style: { "--mrt-base-background-color": "rgb(33, 24, 44)" },
    }),
    initialState: {
      showGlobalFilter: true,
    },
    state: {
      isLoading,
      sorting,
      density: "xs",
    },
  });

  const handleRowClick = (clickedClient) => {
    // Search for the client in the list of clients by accountName
    const foundClient = clients.find(
      (client) => client.label === clickedClient.accountName
    );

    if (foundClient) {
      setSelectedClient(foundClient);
      setIsModalOpen(true);
    } else {
      console.log("Client not found:", clickedClient.accountName);
      // Optionally handle the case where no client is found
    }
  };

  return (
    <>
      <Flex direction="column" pt={{ base: "150px", lg: "75px" }}>
        <MantineProvider
          theme={{
            colorScheme: useColorModeValue("light", "dark"),
            colors: {
              // override dark colors to change them for all components
              dark: [
                "#d5d7e0",
                "#acaebf",
                "#8c8fa3",
                "#666980",
                "#4A5058",
                "#343A40",
                "#343A40",
                "#4A5058",
                "#0c0d21",
                "#01010a",
              ],
              light: [],
            },
          }}
        >
          <Global
            styles={{
              ".mantine-TableHead": {
                zIndex: "1 !important",
              },
              ".chakra-MenuList": {
                zIndex: "9999 !important",
                position: "absolute !important",
              },
              ".chakra-Menu__menu-list": {
                zIndex: "9999 !important",
                position: "absolute !important",
              },
            }}
          />
          <MantineReactTable table={table} />
        </MantineProvider>
        <ClientModal
          props={selectedClient}
          onClose={() => setIsModalOpen(false)}
          isOpen={isModalOpen}
          onUpdate={onClientUpdate}
          refresh={handleClientUpdate}
        />
      </Flex>
    </>
  );
}

export default Clients;
